// src/components/employee/EmployeeLogin.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Container
} from '@mui/material';
import {
  Work as WorkIcon,
  Person as PersonIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useEmployeeAuth } from '../../hooks/useEmployeeAuth';

export default function EmployeeLogin() {
  const navigate = useNavigate();
  const { login, loading, error } = useEmployeeAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email || !formData.password) {
      setFormError('Please fill in all fields');
      return;
    }

    try {
      await login(formData.email, formData.password);
      
      // Let ProtectedEmployeeRoute handle navigation based on isFirstLogin
      // Navigate to a protected route and let the protection logic handle the rest
      navigate('/employee/dashboard');
    } catch (err) {
      setFormError(err.message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        py: 3,
        color: '#000',
        "& *": {
          colorScheme: "light !important",
        },
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          {/* Logo/Header */}
          <Box sx={{ textAlign: 'center' }}>
            <WorkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              ShiftWorks
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Employee Dashboard Access
            </Typography>
          </Box>

          {/* Login Card */}
          <Card sx={{ 
            width: '100%', 
            maxWidth: 400,
            bgcolor: '#ffffff',
            color: '#000',
            "& .MuiTextField-root": {
              "& .MuiOutlinedInput-root": {
                bgcolor: '#ffffff',
                color: '#000',
              },
            },
            "& .MuiTypography-root": {
              color: '#000 !important',
            },
            "& .MuiFormLabel-root": {
              color: '#666 !important',
            },
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
                Employee Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                Access your personal schedule and shifts
              </Typography>

              {(error || formError) && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {formError || error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <TextField
                    name="email"
                    type="email"
                    label="Work Email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    disabled={loading}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />

                  <TextField
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    disabled={loading}
                    InputProps={{
                      startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Stack>
              </form>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  First Time?
                </Typography>
              </Divider>

              {/* First Time Login Info */}
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  First time logging in?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Use your work email and the default password:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  Password: Welcome1
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You'll be asked to change it after first login.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Footer */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Need management access? <Link to="/login" style={{ color: 'inherit' }}>Admin Portal</Link>
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
